"use client";

import { useState } from "react";
import {
  Notice,
  Panel,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import { PosterResults } from "@/components/tools/research-writing/poster-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  DESIGN_SCALES,
  type DesignScale,
  type DistanceUnit,
  DPI_OPTIONS,
  type LengthUnit,
  type Orientation,
  type PosterInput,
  planPoster,
  type SizeKey,
  textShareFraction,
} from "@/lib/tool-kit/research-writing/poster-size";

/** English strings. A translated content file overrides these through `ui`. */
export const RESEARCH_POSTER_SIZE_CALCULATOR_STRINGS = {
  sizeHeading: "Poster size",
  sizeLabel: "Size",
  sizes: {
    a0: "A0 — 841 × 1189 mm",
    a1: "A1 — 594 × 841 mm",
    a2: "A2 — 420 × 594 mm",
    a3: "A3 — 297 × 420 mm",
    in24x36: "24 × 36 in",
    in30x40: "30 × 40 in",
    in36x48: "36 × 48 in",
    custom: "Custom size",
  },
  orientationLabel: "Orientation",
  orientations: { portrait: "Portrait", landscape: "Landscape" },
  unitLabel: "Lengths in",
  units: { in: "Inches", cm: "Centimetres" },
  unitShort: { in: "in", cm: "cm", mm: "mm", ft: "ft", m: "m", pt: "pt" },
  customWidthLabel: "Width",
  customHeightLabel: "Height",
  customPlaceholder: "e.g. 48",
  customPrompt: "Enter a width and a height to see the plan.",
  distancePrompt:
    "Enter a viewing distance or a body text size to see type sizes and the word budget.",
  viewHeading: "Viewing distance and text",
  distanceLabel: "Viewing distance",
  distancePlaceholder: "6",
  distanceHint:
    "How far away a reader stands. UC Davis suggests text and figures legible from about 5–7 feet (1.5–2 m).",
  distanceUnitLabel: "Distance unit",
  distanceUnits: { ft: "Feet", m: "Metres" },
  bodyLabel: "Body text size (points, optional)",
  bodyPlaceholder: "Leave blank to use the guide",
  bodyHint:
    "Type your own size to rescale the other roles and the word budget around it.",
  shareLabel: "Share of the poster given to text (%)",
  sharePlaceholder: "20",
  shareHint:
    "UC Davis suggests roughly 20% text, 40% figures and 40% empty space.",
  printHeading: "Print file",
  scaleLabel: "Design the file at",
  scales: {
    "1": "Full size (100%)",
    "0.5": "Half size (50%)",
    "0.25": "Quarter size (25%)",
  },
  scaleHint:
    "A poster longer than PowerPoint allows is designed small and enlarged at the printer.",
  imageWidthLabel: "Image width on the poster (optional)",
  imageHeightLabel: "Image height (optional)",
  imagePlaceholder: "e.g. 12",
  dpiLabel: "Resolution at final size (dpi)",
  dpiOptions: {
    "100": "100 dpi",
    "150": "150 dpi",
    "200": "200 dpi",
    "300": "300 dpi",
  },
  dpiHint:
    "Purdue’s department guide asks for 300 dpi or more. Your print shop’s requirement comes first.",
  resultsHeading: "Your poster plan",
  finalSize: "Final size",
  metric: "In millimetres",
  shape: "Shape (long ÷ short side)",
  shapeA:
    "The ISO 216 A-series has a long-to-short ratio of √2 ≈ 1.4142, so each size is half the area of the one above and the same shape.",
  bodyCard: "Body text",
  sizeText: "{w} × {h} {unit}",
  sizeMm: "{w} × {h} mm",
  typeHeading: "Type sizes",
  typeHead: {
    role: "Role",
    final: "At final size",
    design: "In your design file",
  },
  roles: {
    title: "Title",
    authors: "Authors and affiliations",
    headings: "Section headings",
    body: "Body text",
    captions: "Captions and references",
  },
  typeNote:
    "Proportions follow UC Davis’s 85 : 56 : 36 : 24 : 18 point set, scaled to your body size. Purdue calls 24 pt an absolute minimum for body text and suggests 36 pt or more where you can.",
  bodyFromGuide:
    "Body text is {pt} pt, from the UC Davis distance guide (never below 24 pt).",
  bodyFromYou: "Body text is {pt} pt, the size you entered.",
  guideHeading: "Legibility guides at {distance}",
  guideHead: { source: "Guide", size: "Suggested size" },
  guideRows: {
    ucDavis: "UC Davis Undergraduate Research Center",
    scienceBuddies: "Science Buddies, via Washington University’s Knight ADRC",
  },
  guideNote:
    "Two published university guides, which differ because they set different bars (UC Davis for legible text, Science Buddies for a minimum comfortable size). They are guidance, not a standard.",
  wordsHeading: "Word budget",
  fitText:
    "one: About {words} word fits in {share} of the poster at {pt} pt.|other: About {words} words fit in {share} of the poster at {pt} pt.",
  ceilingText:
    "one: Aim for no more than {words} word — Purrington’s guide suggests 500–800.|other: Aim for no more than {words} words — Purrington’s guide suggests 500–800.",
  readText: "That reads in about {minutes} minutes at 238 words per minute.",
  wordsNote:
    "The fit is an estimate from average letter widths at a 1.2 line spacing; a narrow font fits more. Fewer words almost always read better.",
  printSubheading: "Printing",
  designText:
    "Design the file at {w} × {h} {unit} ({percent}% of final size), then print at {enlarge}%.",
  fullSizeText: "Design the file at the final size, {w} × {h} {unit}.",
  pptOk:
    "PowerPoint accepts a slide up to 56 in (142.24 cm) a side, so this fits.",
  pptTooBig:
    "PowerPoint stops at 56 in (142.24 cm) a side, and this is larger. Design at {percent}% and enlarge to {enlarge}% at print.",
  pptNever:
    "Even a quarter-size file is over PowerPoint’s 56 in (142.24 cm) limit: use a layout program such as Illustrator, InDesign or Affinity Publisher.",
  imageText:
    "An image printed {w} × {h} {unit} at {dpi} dpi needs at least {wpx} × {hpx} pixels.",
  imageWidthOnly:
    "An image printed {w} {unit} wide at {dpi} dpi needs at least {wpx} pixels across.",
  copy: "Copy the plan",
  copied: "Copied!",
  summaryTitle: "Research poster plan",
  summaryWords:
    "one: Word budget: up to {words} word|other: Word budget: up to {words} words",
  summaryLines: {
    size: "Size: {size} ({mm})",
    distance: "Viewing distance: {distance}",
    body: "Body text: {pt} pt",
    design: "Design file: {w} × {h} {unit}, print at {enlarge}%",
  },
} as const;

const EMPTY: PosterInput = {
  size: "in36x48",
  orientation: "landscape",
  unit: "in",
  customWidth: "",
  customHeight: "",
  distance: "6",
  distanceUnit: "ft",
  bodyPt: "",
  textShare: "",
  scale: 1,
  imageWidth: "",
  imageHeight: "",
  dpi: "300",
};

const SIZE_KEYS: readonly SizeKey[] = [
  "a0",
  "a1",
  "a2",
  "a3",
  "in24x36",
  "in30x40",
  "in36x48",
  "custom",
];
const ISO_SIZES: ReadonlySet<SizeKey> = new Set(["a0", "a1", "a2", "a3"]);

const LENGTH_UNITS: readonly LengthUnit[] = ["in", "cm"];
const DISTANCE_UNITS: readonly DistanceUnit[] = ["ft", "m"];
const ORIENTATIONS: readonly Orientation[] = ["portrait", "landscape"];
type ScaleKey = "1" | "0.5" | "0.25";

export default function ResearchPosterSizeCalculatorWidget() {
  const strings = useToolUi(RESEARCH_POSTER_SIZE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<PosterInput>(EMPTY);
  const plan = planPoster(input);
  const set =
    <K extends keyof PosterInput>(key: K) =>
    (value: PosterInput[K]) =>
      setInput((previous) => ({ ...previous, [key]: value }));
  const unitName = strings.unitShort[input.unit];
  const distanceName = strings.unitShort[input.distanceUnit];

  return (
    <div className="space-y-8">
      <Panel title={strings.sizeHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="ps-size"
            label={strings.sizeLabel}
            onChange={set("size")}
            options={SIZE_KEYS.map((value) => ({
              value,
              label: strings.sizes[value],
            }))}
            value={input.size}
          />
          <SelectField
            id="ps-unit"
            label={strings.unitLabel}
            onChange={set("unit")}
            options={LENGTH_UNITS.map((value) => ({
              value,
              label: strings.units[value],
            }))}
            value={input.unit}
          />
        </div>
        {input.size === "custom" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="ps-width"
              label={`${strings.customWidthLabel} (${unitName})`}
              numeric
              onChange={set("customWidth")}
              placeholder={strings.customPlaceholder}
              value={input.customWidth}
            />
            <TextField
              id="ps-height"
              label={`${strings.customHeightLabel} (${unitName})`}
              numeric
              onChange={set("customHeight")}
              placeholder={strings.customPlaceholder}
              value={input.customHeight}
            />
          </div>
        ) : (
          <PillGroup
            label={strings.orientationLabel}
            name="ps-orientation"
            onChange={set("orientation")}
            options={ORIENTATIONS.map((value) => ({
              value,
              label: strings.orientations[value],
            }))}
            value={input.orientation}
          />
        )}
      </Panel>

      <Panel title={strings.viewHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={strings.distanceHint}
            id="ps-distance"
            label={`${strings.distanceLabel} (${distanceName})`}
            numeric
            onChange={set("distance")}
            placeholder={strings.distancePlaceholder}
            value={input.distance}
          />
          <SelectField
            id="ps-distance-unit"
            label={strings.distanceUnitLabel}
            onChange={set("distanceUnit")}
            options={DISTANCE_UNITS.map((value) => ({
              value,
              label: strings.distanceUnits[value],
            }))}
            value={input.distanceUnit}
          />
          <TextField
            hint={strings.bodyHint}
            id="ps-body"
            label={strings.bodyLabel}
            numeric
            onChange={set("bodyPt")}
            placeholder={strings.bodyPlaceholder}
            value={input.bodyPt}
          />
          <TextField
            hint={strings.shareHint}
            id="ps-share"
            label={strings.shareLabel}
            numeric
            onChange={set("textShare")}
            placeholder={strings.sharePlaceholder}
            value={input.textShare}
          />
        </div>
      </Panel>

      <Panel title={strings.printHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            hint={strings.scaleHint}
            id="ps-scale"
            label={strings.scaleLabel}
            onChange={(value) => set("scale")(Number(value) as DesignScale)}
            options={DESIGN_SCALES.map((value) => ({
              value: String(value),
              label: strings.scales[String(value) as ScaleKey],
            }))}
            value={String(input.scale)}
          />
          <SelectField
            hint={strings.dpiHint}
            id="ps-dpi"
            label={strings.dpiLabel}
            onChange={set("dpi")}
            options={DPI_OPTIONS.map((value) => ({
              value,
              label: strings.dpiOptions[value],
            }))}
            value={input.dpi}
          />
          <TextField
            id="ps-image-width"
            label={`${strings.imageWidthLabel} (${unitName})`}
            numeric
            onChange={set("imageWidth")}
            placeholder={strings.imagePlaceholder}
            value={input.imageWidth}
          />
          <TextField
            id="ps-image-height"
            label={`${strings.imageHeightLabel} (${unitName})`}
            numeric
            onChange={set("imageHeight")}
            value={input.imageHeight}
          />
        </div>
      </Panel>

      <section aria-live="polite" className="space-y-6">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {plan.size ? (
          <PosterResults
            fmt={fmt}
            input={input}
            locale={locale}
            plan={plan}
            share={textShareFraction(input.textShare)}
            strings={strings}
          />
        ) : (
          <Notice>{strings.customPrompt}</Notice>
        )}
        {plan.size && plan.bodyPt === null ? (
          <Notice>{strings.distancePrompt}</Notice>
        ) : null}
        {ISO_SIZES.has(input.size) ? (
          <p className={HINT}>{strings.shapeA}</p>
        ) : null}
      </section>
    </div>
  );
}
